/**
 * Epic #228 P4 — the effect interpreter of `reduceDraftSaveQueue`. The only
 * place that touches promises, the scheduler and error normalization. The
 * `save`, `fetchHead`, `scheduler` and `newMutationId` dependencies are
 * injected, so the admin hook binds the Supabase services and a test drives
 * the queue with deferred promises and a fake clock.
 */
import {
  toWebsiteTemplateServiceError,
  type WebsiteTemplateServiceErrorCode,
} from '../../services/website-templates-errors';
import type { WebsiteSlotValue } from '../../types/website-templates';
import type { WebsiteSlotBatch } from './slots';
import {
  draftSaveHasWork,
  initialDraftSaveQueueState,
  reduceDraftSaveQueue,
  type DraftSaveCommitMode,
  type DraftSaveEffect,
  type DraftSaveHead,
  type DraftSavePayload,
  type DraftSaveQueueEvent,
  type DraftSaveQueueState,
  type DraftSaveSnapshot,
  type DraftSaveStatus,
} from './draft-save-queue';

export interface DraftSaveScheduler {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface DraftSaveQueueDeps {
  /** The head revision the first save CASes on. */
  readonly base: string;
  /** Accepted for the plan's call shape; first-touch values come from `enqueue(…, seen)`. */
  readonly baseSlots?: Readonly<Record<string, WebsiteSlotValue>>;
  readonly save: (payload: DraftSavePayload) => Promise<{ readonly draftRevisionId: string }>;
  readonly fetchHead: () => Promise<DraftSaveHead>;
  readonly scheduler?: DraftSaveScheduler;
  readonly newMutationId?: () => string;
  readonly onSaved?: (result: {
    readonly draftRevisionId: string;
    readonly slotIds: readonly string[];
  }) => void;
  readonly onSlotRejected?: (slotId: string, code: WebsiteTemplateServiceErrorCode) => void;
  /** Diagnostics only: a code and a short detail, never a value. */
  readonly log?: (code: string, detail?: string) => void;
}

export type DraftSaveFlushResult =
  | { readonly ok: true; readonly base: string }
  | { readonly ok: false; readonly status: DraftSaveStatus };

/** `runExclusive` could not bring the queue to a saved state first. */
export class DraftSaveFlushError extends Error {
  readonly status: DraftSaveStatus;

  constructor(status: DraftSaveStatus) {
    super(`draft_save_flush_${status}`);
    this.name = 'DraftSaveFlushError';
    this.status = status;
  }
}

export interface DraftSaveQueue {
  getSnapshot(): DraftSaveSnapshot;
  subscribe(listener: () => void): () => void;
  /** RULE 0 is the caller's: only a value the region schema accepts is enqueued. */
  enqueue(batch: WebsiteSlotBatch, mode: DraftSaveCommitMode, seen: WebsiteSlotBatch): void;
  /**
   * Sends what is pending (also while paused) and resolves once the queue
   * settles. Edits made while the flush is running are drained with it, so
   * the resolved `base` holds the last keystroke before the caller goes on.
   */
  flush(): Promise<DraftSaveFlushResult>;
  /**
   * A flag, not a counter: `resume()` un-pauses whoever paused. The publish
   * flow and `runExclusive` are modal and never overlap today; a second
   * concurrent pauser needs a depth counter here first.
   */
  pause(): void;
  resume(): void;
  /** From `failed`: resends the parked payload verbatim. From `conflict`: nothing (use `reload`). */
  retry(): Promise<DraftSaveFlushResult>;
  /**
   * PD9: adopts the newer head, then saves the merchant's values on top of it.
   * Not `ok` when the head could not be fetched (also with nothing to save)
   * or the values could not be saved on top; `getSnapshot().hasFailedPayload`
   * tells the two apart.
   */
  reload(): Promise<DraftSaveFlushResult>;
  /**
   * PD10: pause → flush → `fn(base)` → adopt the head `fn` returns → resume
   * (always). A flush that does not reach a saved state throws
   * `DraftSaveFlushError` before `fn` runs.
   */
  runExclusive<T>(
    fn: (base: string) => Promise<{ readonly result: T; readonly head: DraftSaveHead }>,
  ): Promise<T>;
  /** Refused (logged) while the queue has work: the next save rebases instead. */
  adoptHead(head: DraftSaveHead): void;
  /** "Leave without saving": drops pending, parked and rejected values. */
  discard(): void;
  /** The editor state now shows this head (the admin hook calls it on every head change). */
  observeHead(revisionId: string): void;
  /** Cancels timers; a result that lands later changes nothing. */
  dispose(): void;
}

const defaultScheduler: DraftSaveScheduler = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

function defaultMutationId(): string {
  return crypto.randomUUID();
}

function start<T>(task: () => Promise<T>): Promise<T> {
  try {
    return task();
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
}

export function createDraftSaveQueue(deps: DraftSaveQueueDeps): DraftSaveQueue {
  const scheduler = deps.scheduler ?? defaultScheduler;
  const newMutationId = deps.newMutationId ?? defaultMutationId;
  let state: DraftSaveQueueState = initialDraftSaveQueueState({ base: deps.base });
  let disposed = false;
  const listeners = new Set<() => void>();
  const waiters: Array<(result: DraftSaveFlushResult) => void> = [];
  let debounceHandle: unknown = null;
  let backoffHandle: unknown = null;
  /** The head fetch of the current `reload()` failed: its result must say so even with no work. */
  let reloadFetchFailed = false;

  const clearDebounce = (): void => {
    if (debounceHandle === null) return;
    scheduler.clearTimeout(debounceHandle);
    debounceHandle = null;
  };
  const clearBackoff = (): void => {
    if (backoffHandle === null) return;
    scheduler.clearTimeout(backoffHandle);
    backoffHandle = null;
  };

  /** Null while a request, a fetch or a drain is outstanding. */
  const settledResult = (): DraftSaveFlushResult | null => {
    if (state.draining || state.inFlight || state.fetching !== null) return null;
    if (!draftSaveHasWork(state) && !state.conflict && state.status !== 'failed') {
      return { ok: true, base: state.base };
    }
    const status: DraftSaveStatus = state.conflict
      ? 'conflict'
      : state.hasFailedPayload
        ? 'failed'
        : state.status;
    return { ok: false, status };
  };

  const settleWaiters = (): void => {
    if (waiters.length === 0) return;
    const result = settledResult();
    if (result === null) return;
    for (const resolve of waiters.splice(0)) resolve(result);
  };

  function dispatch(event: DraftSaveQueueEvent): void {
    if (disposed) return;
    const reduced = reduceDraftSaveQueue(state, event, newMutationId);
    const changed = reduced.state !== state;
    state = reduced.state;
    for (const effect of reduced.effects) run(effect);
    if (changed) for (const listener of listeners) listener();
    settleWaiters();
  }

  function run(effect: DraftSaveEffect): void {
    switch (effect.type) {
      case 'armTimer':
        clearDebounce();
        debounceHandle = scheduler.setTimeout(() => {
          debounceHandle = null;
          dispatch({ type: 'timerFired' });
        }, effect.ms);
        return;
      case 'cancelTimer':
        clearDebounce();
        return;
      case 'armBackoff':
        clearBackoff();
        backoffHandle = scheduler.setTimeout(() => {
          backoffHandle = null;
          dispatch({ type: 'backoffElapsed' });
        }, effect.ms);
        return;
      case 'cancelBackoff':
        clearBackoff();
        return;
      case 'send': {
        // The request leaves synchronously; a throw is a failed request.
        const { clientMutationId } = effect.payload;
        void start(() => deps.save(effect.payload)).then(
          (result) =>
            dispatch({
              type: 'saveSucceeded',
              clientMutationId,
              draftRevisionId: result.draftRevisionId,
            }),
          (error: unknown) => {
            const normalized = toWebsiteTemplateServiceError(error);
            dispatch({
              type: 'saveFailed',
              clientMutationId,
              code: normalized.code,
              slotId: normalized.slotId,
            });
          },
        );
        return;
      }
      case 'fetchHead':
        void start(() => deps.fetchHead()).then(
          (head) => dispatch({ type: 'headFetched', head }),
          (error: unknown) => {
            reloadFetchFailed = true;
            dispatch({ type: 'headFetchFailed', code: toWebsiteTemplateServiceError(error).code });
          },
        );
        return;
      case 'saved':
        deps.onSaved?.({ draftRevisionId: effect.draftRevisionId, slotIds: effect.slotIds });
        return;
      case 'slotRejected':
        deps.onSlotRejected?.(effect.slotId, effect.code);
        return;
      case 'log':
        deps.log?.(effect.code, effect.detail);
        return;
    }
  }

  const awaitSettled = (event: DraftSaveQueueEvent): Promise<DraftSaveFlushResult> =>
    new Promise((resolve) => {
      if (disposed) {
        resolve({ ok: false, status: state.status });
        return;
      }
      waiters.push(resolve);
      dispatch(event);
    });

  const queue: DraftSaveQueue = {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    enqueue: (batch, mode, seen) => dispatch({ type: 'enqueue', batch, mode, seen }),
    flush: () => awaitSettled({ type: 'flushRequested' }),
    pause: () => dispatch({ type: 'pause' }),
    resume: () => dispatch({ type: 'resume' }),
    retry: () => awaitSettled({ type: 'retry' }),
    reload: async () => {
      reloadFetchFailed = false;
      const result = await awaitSettled({ type: 'reload' });
      if (result.ok && reloadFetchFailed) {
        return { ok: false, status: state.conflict ? 'conflict' : 'failed' };
      }
      return result;
    },
    runExclusive: async (fn) => {
      queue.pause();
      const flushed = await queue.flush();
      if (!flushed.ok) {
        queue.resume();
        throw new DraftSaveFlushError(flushed.status);
      }
      try {
        const { result, head } = await fn(flushed.base);
        queue.adoptHead(head);
        return result;
      } finally {
        queue.resume();
      }
    },
    adoptHead: (head) => dispatch({ type: 'adoptHead', head }),
    discard: () => dispatch({ type: 'discard' }),
    observeHead: (revisionId) => dispatch({ type: 'headObserved', revisionId }),
    dispose: () => {
      if (disposed) return;
      disposed = true;
      clearDebounce();
      clearBackoff();
      listeners.clear();
      for (const resolve of waiters.splice(0)) resolve({ ok: false, status: state.status });
    },
  };
  return queue;
}
